import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { SectionCard } from '@/components/Chat/ConfirmInfoCard'
import { FieldError } from '@/components/ui/field'
import { CacheKeys } from '@/lib/cache'
import {
  type ContractorAnswerField,
  type ContractorAnswerSection,
  ContractorQuestionKind,
  saveContractorAnswers,
  setConfirmInfoState,
  SKIPPED_ANSWER,
} from '@/models/confirmInfo'
import { fillApplicationErrorMessage } from '@/models/fillApplication'
import type { FillCardForm } from '../App/Customers/FillApplication/useFillCardSave'
import { ContractorAnswerInput, type ContractorDraft } from './ContractorAnswerInput'
import { ReviewSection } from './ReviewSection'

interface ContractorAnswersSectionProps {
  orgId: string
  section: ContractorAnswerSection
  open: boolean
  onOpen: () => void
  onClose: () => void
  /** Told when the dialog gains or loses unsaved edits, so leaving the page can warn. */
  onDirtyChange?: (dirty: boolean) => void
}

/** One part of a contractor's chat answers: listed on the page, edited in a dialog. */
export function ContractorAnswersSection({
  orgId,
  section,
  open,
  onOpen,
  onClose,
  onDirtyChange,
}: ContractorAnswersSectionProps) {
  const queryClient = useQueryClient()
  const editable = section.rows.filter((row) => row.field !== null)
  const saved = useMemo(
    () =>
      Object.fromEntries(section.rows.flatMap((row) => (row.field ? [[row.key, asDraft(row.field)]] : []))),
    [section]
  )
  const [draft, setDraft] = useState<Record<string, ContractorDraft>>(saved)
  // The answers as the dialog opened on them: an edit is a change from these, so a field left alone never saves
  // over an answer another tab changed meanwhile.
  const [opened, setOpened] = useState<Record<string, ContractorDraft>>(saved)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  // Each opening starts from what's saved. Only the opening: a refetch while open must not undo the edits.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraft(saved)
      setOpened(saved)
      setErrors({})
    }
    wasOpen.current = open
  }, [open, saved])

  const changed = editable.filter((row) => JSON.stringify(draft[row.key]) !== JSON.stringify(opened[row.key]))
  const dirty = changed.length > 0
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange])
  const form: FillCardForm = {
    isDirty: dirty,
    saving,
    discard: () => {
      setDraft(opened)
      setErrors({})
    },
    save: async () => {
      setSaving(true)
      setErrors({})
      const answers = Object.fromEntries(
        changed.map((row) => [row.key, asAnswer(row.field!, draft[row.key])])
      )
      try {
        setConfirmInfoState(queryClient, orgId, await saveContractorAnswers(orgId, answers))
        // What was sent is now what's saved, so the section reads clean and leaving no longer warns.
        setOpened(draft)
        // A new legal name renames the account too.
        void queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
        return true
      } catch (failure) {
        setErrors(fieldErrors(failure) ?? { [FORM_ERROR]: fillApplicationErrorMessage(failure) })
        return false
      } finally {
        setSaving(false)
      }
    },
  }

  const summary = (
    <dl className="flex flex-col gap-3">
      {section.rows.map((row) => (
        <div key={row.key} className="flex flex-col gap-0.5">
          <dt className="text-ink-400">{row.question}</dt>
          <dd className="text-foreground wrap-anywhere whitespace-pre-wrap">{row.answer}</dd>
        </div>
      ))}
    </dl>
  )
  if (editable.length === 0) return <SectionCard title={section.title}>{summary}</SectionCard>

  return (
    <ReviewSection
      title={section.title}
      checks={[]}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      form={form}
      summary={summary}
    >
      <div className="flex flex-col gap-5">
        {editable.map((row) => (
          <ContractorAnswerInput
            key={row.key}
            id={`contractor-${row.key}`}
            question={row.question}
            field={row.field!}
            value={draft[row.key] ?? null}
            onChange={(value) => setDraft((current) => ({ ...current, [row.key]: value }))}
            error={errors[row.key]}
          />
        ))}
        <FieldError>{errors[FORM_ERROR]}</FieldError>
      </div>
    </ReviewSection>
  )
}

const FORM_ERROR = '_form'

function asDraft(field: ContractorAnswerField): ContractorDraft {
  if (field.value === SKIPPED_ANSWER) return field.kind === ContractorQuestionKind.MULTI ? [] : ''
  if (field.money) return typeof field.value === 'number' ? field.value : null
  return typeof field.value === 'number' ? String(field.value) : field.value
}

/** What the server takes: an optional answer left empty is skipped, as "Skip for now" does in the chat. */
function asAnswer(field: ContractorAnswerField, draft: ContractorDraft): ContractorAnswerField['value'] {
  const empty = draft === null || draft === '' || (Array.isArray(draft) && draft.length === 0)
  if (empty) return field.optional ? SKIPPED_ANSWER : ''
  return draft
}

/** The server's refusal per question, `{key: message}` (or a list of them), or null when it isn't one. */
function fieldErrors(failure: unknown): Record<string, string> | null {
  const response = (failure as { response?: { status?: number; data?: unknown } } | null)?.response
  if (response?.status !== 400 || !response.data || typeof response.data !== 'object') return null
  const entries = Object.entries(response.data as Record<string, unknown>).flatMap(([key, messages]) => {
    const message: unknown = Array.isArray(messages) ? messages[0] : messages
    return typeof message === 'string' ? [[key, message]] : []
  })
  return entries.length ? Object.fromEntries(entries) : null
}
